import React from 'react';

import View from "../View/View";
import {
  withFormControl,
  PuzzleButton,
  Container,
  PuzzleToolbar
} from "puzzle";
import { config, tptConfig } from "../Create/Create.config";
import SubmissionAbstract from "../SubmissionAbstract";
import PoliciesManager from "../../util/Policies";
import _ from "lodash";

export type PROPS = {
  completedStatuses: string[],
  submissionMode: string
};

export type STATE = {
  submissionId: string,
  submissionStatus: string,
  submissionStarted: boolean
}

class Edit extends SubmissionAbstract<PROPS, STATE> {
  skipRequestStatuses: string[] = ['REQUEST_PENDING', 'PENDING'];
  static defaultProps = {
    completedStatuses: ['PROCESSED', 'DELIVERED', 'STOPPED'],
    submissionMode: 'EDIT',
    dispatcherUpdateStates: {
      uploadFlowState: {}
    }
  };

  /**
   * Method returns true if there are some cases pointing that quotes will be withdrawn. False otherwise
   *
   * @param submissionSnapshot
   * @param parsedData
   * @param quoteStatus
   */
  hasWithdrawnQuoteChanges = (submissionSnapshot: any, parsedData: any, quoteStatus: string | null) => {
    let hasChanges = (quoteStatus && !this.skipRequestStatuses.includes(quoteStatus));
    if (hasChanges) {
      hasChanges = (submissionSnapshot.sourceLanguage !== parsedData.sourceLanguage) || (submissionSnapshot.batchInfos.length !== parsedData.batchInfos.length);
      if (!hasChanges) {
        let currentBatch;
        let item = parsedData.batchInfos.length;
        while (!hasChanges && item--) {
          currentBatch = parsedData.batchInfos[item];
          hasChanges = !currentBatch.batchId || (currentBatch.operation === "DELETE") || this.hasWithdrawnQuoteChangesPerBatch(currentBatch, submissionSnapshot.batchInfos)
        }
      }
    }
    return hasChanges;
  };

  /**
   *
   * Method returns true if there are some cases pointing that quotes will be withdrawn (in batch level). False otherwise
   *
   * @param currentBatchInfos
   * @param snapBatches
   */
  hasWithdrawnQuoteChangesPerBatch = (currentBatchInfos: any, snapBatches: any[]) => {
    const { workflowId, deletedDocumentIds, temporaryFiles, targetLanguageInfos, batchId } = currentBatchInfos;
    const snapBatch = _.find(snapBatches, { batchId: batchId });
    return workflowId !== snapBatch?.workflowId || deletedDocumentIds.length || temporaryFiles.length || (targetLanguageInfos.length !== snapBatch.targetLanguageInfos.length);
  };

  /**
   * Cancel button handler, prompts warning window and redirects to previous path
   */
  onCancel = () => {
    this.dispatch('checkForChangesBeforeRedirect', '', { leaveAction: 'goBack' })
  };

  renderBody() {
    const {
      submissionStatus,
      submissionStarted,
      submissionId,
      uploadFlowState
    } = this.state;
    const {
      batchesWithFiles,
      isMultiBatch,
      mslaLevel,
      project,
      specificDate,
      uploadedFiles
    } = this.props.form.getData();

    const uploadedFilesExist = isMultiBatch ? batchesWithFiles : !!uploadedFiles?.length;
    const hasSubmissionStartPolicy = PoliciesManager.inProject(project)?.hasSubmissionStartPolicy;
    const hasSubmissionAnalyzePolicy = PoliciesManager.inProject(project)?.hasSubmissionAnalyzePolicy;
    const hasSelectVendorsButtonsDialogPolicy = PoliciesManager.inProject(project)?.hasSelectVendorsButtonsDialogPolicy;
    const fileUploadInProgress = !this.isAllUploadEnd();
    const mslaLevelCheck = mslaLevel ? !!specificDate : true;

    return (
      <Container
        vBox
        className={'tp-page tp-edit-page'}
      >
        <Container
          alignItemsCenter
          className={'tp-page-header'}
          displayFlex
          justifyContentBetween
          wrap
          htmlTag={'section'}
          tabIndex={0}
        >
          <Container
            className={'tp-page-header-wrap'}
            hBox
            wrap
            alignItemsCenter
          >
            <h2 className={'tp-page-title'}>
              {this.translate('editSubmission')}
            </h2>
          </Container>
          <PuzzleToolbar className={'tp-page-header-action-toolbar'}>
            <PuzzleButton
              fillMode={'outline'}
              onClick={this.onCancel}
            >
              {this.translate('cancel')}
            </PuzzleButton>
            {uploadedFilesExist && !submissionStarted && hasSubmissionStartPolicy && hasSelectVendorsButtonsDialogPolicy ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'selectSuppliersSubmissionAction', {
                  analyze: false,
                  project,
                  submissionId
                })}
                themeColor={'primary'}
              >
                {this.translate('selectSuppliers')}
              </PuzzleButton>
              : null}
            <PuzzleButton
              disabled={fileUploadInProgress}
              onClick={this.triggerSubmissionAction.bind(this, 'saveEditSubmission', {
                analyze: false,
                submissionId,
                staticFields: this.staticFields
              })}
              type="submit"
              themeColor={'primary'}>
              {this.translate('save')}
            </PuzzleButton>
            {uploadedFilesExist && (_.includes(['WAITING', 'PREFLIGHT'], submissionStatus) || (submissionStatus === 'READY' && !submissionStarted)) && hasSubmissionAnalyzePolicy ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'saveEditSubmission', { analyze: true, submissionId })}
                themeColor={'primary'}
              >
                {this.translate('analyze')}
              </PuzzleButton>
              : null}
            {uploadedFilesExist && !submissionStarted && hasSubmissionStartPolicy && mslaLevelCheck ?
              <PuzzleButton
                disabled={fileUploadInProgress}
                onClick={this.triggerSubmissionAction.bind(this, 'startSubmissionDialog', {
                  analyze: false,
                  project,
                  submissionId
                })}
                themeColor={'primary'}
              >
                {this.translate('start')}
              </PuzzleButton>
              : null}
          </PuzzleToolbar>
        </Container>
        <View
          {...this.props}
          fileUploadInProgress={fileUploadInProgress}
          projectPolicies={PoliciesManager.inProject(project)}
          submissionStatus={submissionStatus}
          uploadFlowState={uploadFlowState}
        />
      </Container>
    );
  }
}

const Tpt = withFormControl(tptConfig, false)(Edit);
const Client = withFormControl(config, false)(Edit);

const EditView = (props: { tptEnabled: boolean }): JSX.Element => {
  const mode = props.tptEnabled ? <Tpt tptEnabled={true} /> : <Client tptEnabled={false} />;
  return mode;
};

export default EditView;
